import { SparklesIcon } from "lucide-react";
import { useEffect, useImperativeHandle, useMemo, useState, type Ref } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useFuenteDeLaDescripcion } from "@/hooks/useFuenteDeLaDescripcion";
import { useRellenarInstalacionesConIa, useResolverElRelleno } from "@/hooks/useRellenoConIa";
import { getApiErrorMessage } from "@/lib/api";
import { CAMPOS_CON_ASISTENCIA } from "@/lib/ia";
import type { ObraPropuesta } from "@/lib/rellenoConIa";
import type { CamposDeLaFila, GuardarLaPestanaInput, Informe } from "@/lib/informes";
import { formatearCifra, formatearMonto } from "@/lib/montos";
import { cn } from "@/lib/utils";

import { BarraDeGuardado } from "./BarraDeGuardado";
import { CampoConAsistencia } from "./CampoConAsistencia";
import { DialogoDeInstalacion } from "./DialogoDeInstalacion";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { FilasPropuestas } from "./FilasPropuestas";
import { FuenteDeLaPropuesta } from "./FuenteDeLaPropuesta";
import { ListaDeInstalaciones } from "./ListaDeInstalaciones";
import { NotasDeLaPestana } from "./NotasDeLaPestana";
import {
  AvisoDeLoRellenado,
  BotonDeRellenoConIa,
  type AvisoDelRelleno,
} from "./RellenoConIa";
import {
  formularioDesdeLaObraPropuesta,
  resumenDeLoPropuestoEnInstalaciones,
  yaEstaEnElCuadroDeObras,
} from "./instalacionesConIa";
import { camposDesdeInstalacion } from "./instalaciones";
import {
  campoDeLaDescripcionDeInstalaciones,
  itemsPorEnlazarDeInstalaciones,
  seccionDeInstalaciones,
  tituloDelAviso,
  type TerrenoDeLaLista,
} from "./instalacionesTerreno";
import { modoDelTerreno } from "./cuadroDeConstrucciones";
import { VisorDeFotos } from "./FotosDeTerreno";
import { TerrenoDeLaDescripcion } from "./TerrenoDeLaDescripcion";
import { fotosDelVisor } from "./registroDeTerreno";
import type { EstadoDelTerreno } from "./terrenoDeAntecedentes";

/** Lo que la pantalla puede pedirle a la pestaña desde el aviso de salida. */
export interface PestanaDeInstalacionesHandle {
  guardar: () => void;
  marcarGuardado: () => void;
}

interface PestanaDeInstalacionesProps {
  informe: Informe;
  /**
   * Lo que el encargado sincronizó desde la App de terreno. Solo se LEE: las
   * instalaciones que creó llegan como filas TERRENO y lo registrado en cada
   * fila se ve bajo ella. Sin él (módulo apagado) la pestaña se dibuja sin
   * ninguna pieza de terreno.
   */
  terreno?: EstadoDelTerreno;
  /** Quién tiene asignada la visita: da el nombre en «aún sin sincronizar». */
  asignado?: string | null;
  /** El "Guardar cambios" de la pestaña está en vuelo. */
  guardando: boolean;
  /** Hay una operación de fila en vuelo (alta, corrección, borrado, foto). */
  ocupada: boolean;
  ref?: Ref<PestanaDeInstalacionesHandle>;
  onCambios?: (hayCambios: boolean) => void;
  /** Crea la instalación y, con el id que devuelve, sube sus fotos. */
  onAgregar: (campos: CamposDeLaFila, fotos: File[]) => void;
  onCorregir: (filaId: number, campos: CamposDeLaFila) => void;
  onQuitar: (filaId: number) => void;
  /** «Mantener» en el aviso de terreno «No está en el predio». */
  onMantener?: (filaId: number) => void;
  onSubirFoto: (filaId: number, archivo: File) => void;
  onQuitarFoto: (filaId: number, fotoId: number) => void;
  /** Guarda la leyenda libre de una foto de la fila (en el acto, fuera del formulario). */
  onLeyendaDeFoto?: (filaId: number, fotoId: number, leyenda: string | null) => void;
  onGuardar: (input: GuardarLaPestanaInput) => void;
}

/**
 * La pestaña "Instalaciones y obras complementarias": la lista de losas,
 * redes, cierros y obras civiles del predio, su descripción general y la
 * casilla que declara que no hay ninguna. Sigue el rediseño de Claude Design
 * (05-10-2026), el mismo que ya tiene Construcciones.
 *
 * ES LA HERMANA MÁS SIMPLE DE LAS PESTAÑAS CON CUADRO, y las dos ausencias la
 * definen: NO hay vida útil ni ajuste comercial. Los dos valores en UF se
 * cargan derecho y son independientes —el de reposición es lo que cuesta hacer
 * la obra de nuevo y el comercial lo que aporta al predio—, mientras que en
 * maquinaria y construcciones el segundo se DERIVA del primero. Por eso la
 * lista tiene DOS totales y no uno.
 *
 * LA CANTIDAD ES DECIMAL, que es la otra diferencia con maquinaria: las
 * unidades de una máquina se cuentan y las de una losa se MIDEN.
 *
 * EL CRUCE CON LA VISITA DE TERRENO ES EL DE MAQUINARIA: las instalaciones
 * que el encargado creó en la App llegan como filas TERRENO que se adoptan por
 * el formulario de alta, y lo registrado en cada fila se ve bajo ella.
 *
 * DOS GUARDADOS, igual que sus hermanas: cada instalación se escribe en el
 * acto —desde el formulario en línea o desde su modal— y "Guardar cambios"
 * escribe lo que es de la PESTAÑA. POR ESO LAS FILAS NO CUENTAN COMO "CAMBIOS
 * SIN GUARDAR": cuando ese aviso aparece, ya están en el servidor.
 */
export function PestanaDeInstalaciones({
  informe,
  terreno,
  asignado = null,
  guardando,
  ocupada,
  ref,
  onCambios,
  onAgregar,
  onCorregir,
  onQuitar,
  onMantener,
  onSubirFoto,
  onQuitarFoto,
  onLeyendaDeFoto,
  onGuardar,
}: PestanaDeInstalacionesProps) {
  const filas = useMemo(() => informe.instalaciones ?? [], [informe.instalaciones]);
  const estado = informe.secciones?.instalaciones;

  /* --- Lo de terreno --- */

  const registroDeTerreno = terreno?.estado === "ok" ? terreno.registro : undefined;
  const seccionDeTerreno = seccionDeInstalaciones(registroDeTerreno);
  const porEnlazar = useMemo(
    () => itemsPorEnlazarDeInstalaciones(registroDeTerreno, filas),
    [registroDeTerreno, filas],
  );
  const fotosDelRegistro = useMemo(
    () => (seccionDeTerreno ? fotosDelVisor([seccionDeTerreno], "todo") : []),
    [seccionDeTerreno],
  );
  const [fotoAbierta, setFotoAbierta] = useState<string | null>(null);

  const [noAplica, setNoAplica] = useState(estado?.no_aplica ?? false);
  const [descripcion, setDescripcion] = useState(estado?.descripcion_general ?? "");
  const fuenteDelTexto = useFuenteDeLaDescripcion(descripcion);
  const [validada, setValidada] = useState(estado?.descripcion_general_validada ?? false);
  const [notas, setNotas] = useState(informe.notas?.instalaciones ?? "");

  /* La referencia de "lo guardado" es estado y no las props: después de un
     guardado exitoso la base tiene que moverse a lo que quedó en pantalla, y
     las props recién traen ese dato cuando vuelve el refetch. */
  const [base, setBase] = useState({
    noAplica: estado?.no_aplica ?? false,
    descripcion: estado?.descripcion_general ?? "",
    validada: estado?.descripcion_general_validada ?? false,
    notas: informe.notas?.instalaciones ?? "",
  });

  const [propuestas, setPropuestas] = useState<ObraPropuesta[]>([]);

  const [ejecucionPendiente, setEjecucionPendiente] = useState<number | null>(null);

  /*
   * UNA PROPUESTA PENDIENTE ES UN CAMBIO SIN GUARDAR, y una fila ya guardada
   * NO. La distinción es la de siempre —cuando el aviso aparecería, la fila ya
   * está en el servidor— pero lo que la IA propuso existe SOLO en el navegador:
   * cambiar de pestaña lo desmonta (Radix no conserva el contenido inactivo) y
   * se perdía sin que nada lo dijera.
   */
  const cambios =
    Number(noAplica !== base.noAplica) +
    Number(descripcion !== base.descripcion) +
    Number(validada !== base.validada) +
    Number(notas !== base.notas) +
    propuestas.length;
  const hayCambios = cambios > 0;

  useEffect(() => {
    onCambios?.(hayCambios);
  }, [hayCambios, onCambios]);

  useEffect(() => () => onCambios?.(false), [onCambios]);

  useImperativeHandle(ref, () => ({
    guardar,
    marcarGuardado: () => {
      // El panel se vacía porque lo propuesto ya se guardó: dejarlo lo
      // haría ver pendiente sobre filas que la lista ya muestra, y el
      // aviso de cambios sin guardar no se apagaría nunca.
      fuenteDelTexto.olvidar();
      setPropuestas([]);
      setAviso(null);

      setBase({ noAplica, descripcion, validada, notas });

      // GUARDAR LA PESTAÑA TAMBIÉN ACEPTA LA PROPUESTA aunque no se haya
      // agregado ninguna obra: lo que se guardó pudo ser la DESCRIPCIÓN.
      aceptarLaCorrida();
    },
  }));

  /* --- "Rellenar con IA" --- */

  const rellenar = useRellenarInstalacionesConIa(informe.id);
  const resolverElRelleno = useResolverElRelleno();

  const [aviso, setAviso] = useState<AvisoDelRelleno | null>(null);
  const [errorDelRelleno, setErrorDelRelleno] = useState<string | null>(null);

  /** Anota que la propuesta se usó, una sola vez. */
  function aceptarLaCorrida() {
    if (ejecucionPendiente === null) return;

    resolverElRelleno.mutate({ ejecucionId: ejecucionPendiente, resultado: "aceptado" });
    setEjecucionPendiente(null);
  }

  function pedirElRelleno() {
    setErrorDelRelleno(null);
    setAviso(null);

    rellenar.mutate(undefined, {
      onSuccess: ({ ejecucion_id, propuesta, documentos, pendientes, sin_usar }) => {
        setPropuestas(propuesta.instalaciones);
        setEjecucionPendiente(ejecucion_id);

        // LA DESCRIPCIÓN SOLO SE ESCRIBE SI ESTÁ VACÍA, misma regla que en
        // suelos y maquinaria: un texto no se agrega, se pisa — y este lleva
        // casilla "Información validada". Se lee del ESTADO ACTUAL y no de una
        // copia, porque leer el expediente tarda y quien apretó sigue
        // escribiendo.
        const propuso = propuesta.descripcion_general?.trim() ?? "";
        let escribio = false;

        // LA FUENTE DEL TEXTO SE MUESTRA SOLO MIENTRAS EL TEXTO SIGA SIENDO EL
        // PROPUESTO (el hook lo compara): si el campo ya tenía algo, la IA no
        // escribió y no hay fuente que mostrar. Un relleno nuevo reemplaza la
        // anterior.
        fuenteDelTexto.olvidar();
        if (propuso !== "") {
          fuenteDelTexto.registrar(propuso, propuesta.descripcion_general_fuente ?? null);
        }

        if (propuso !== "") {
          setDescripcion((actual) => {
            if (actual.trim() !== "") return actual;

            escribio = true;

            return propuso;
          });
        }

        setAviso({
          resumen: resumenDeLoPropuestoEnInstalaciones(propuesta.instalaciones.length, escribio),
          documentos,
          pendientes,
          sinUsar: sin_usar,
        });
      },

      onError: (e) =>
        setErrorDelRelleno(
          getApiErrorMessage(
            e,
            "No pudimos leer el expediente. Inténtalo de nuevo o carga las obras a mano.",
          ),
        ),
    });
  }

  /** Descarta lo que queda de la propuesta. Lo ya agregado no se toca. */
  function descartarElRelleno() {
    if (ejecucionPendiente !== null) {
      resolverElRelleno.mutate({ ejecucionId: ejecucionPendiente, resultado: "descartado" });
      setEjecucionPendiente(null);
    }

    fuenteDelTexto.olvidar();
    setPropuestas([]);
    setAviso(null);
  }

  /* --- El modal de edición --- */

  /** Qué fila está abierta en el modal, por su id. */
  const [abierta, setAbierta] = useState<number | null>(null);
  // Se lee de la ficha VIVA en cada render: una foto subida desde el modal
  // tiene que aparecer en él sin cerrarlo.
  const enEdicion = filas.find((f) => f.id === abierta) ?? null;

  function guardar() {
    // LO PROPUESTO SE GUARDA ACÁ, no fila por fila. El porqué está en
    // `FilasPropuestas`: quien mira la pantalla va al botón grande, no a
    // los chicos de cada fila. Las que YA están no se repiten.
    propuestas
      .filter((obra) => !yaEstaEnElCuadroDeObras(obra.tipo, filas))
      .forEach((obra) => onAgregar(camposDesdeInstalacion(formularioDesdeLaObraPropuesta(obra)), []));

    onGuardar({
      no_aplica: noAplica,
      // Vaciar el textarea BORRA la descripción: `""` viaja como `null`, que
      // es lo que la columna guarda cuando no hay prosa.
      descripcion_general: descripcion.trim() === "" ? null : descripcion,
      descripcion_general_validada: validada,
      notas_observaciones: notas.trim() === "" ? null : notas,
    });
  }

  /** Vuelve la pestaña a lo último guardado y suelta lo que la IA propuso. */
  function descartarLosCambios() {
    setNoAplica(base.noAplica);
    setDescripcion(base.descripcion);
    setValidada(base.validada);
    setNotas(base.notas);
    descartarElRelleno();
  }

  const hayFilas = filas.length > 0;

  /** Todo lo que la lista necesita del terreno, o `undefined` con el módulo apagado. */
  const terrenoDeLaLista: TerrenoDeLaLista | undefined = registroDeTerreno
    ? {
        informeId: informe.id,
        modo: modoDelTerreno(terreno),
        encargado: registroDeTerreno.encargado?.nombre ?? null,
        items: seccionDeTerreno?.items ?? [],
        porEnlazar,
        onAbrirFoto: setFotoAbierta,
        // "Es la misma que": un PATCH de la fila elegida. El hook relee el
        // registro y la instalación de terreno deja de listarse suelta.
        onVincular: (uuid, filaId) => onCorregir(filaId, { terreno_ref: uuid }),
        onQuitar,
        onMantener,
      }
    : undefined;
  // Con la casilla puesta la lista no se dibuja, y las tarjetas que siguen
  // corren un número para no saltarse el 01.
  const numeroDeLaDescripcion = noAplica ? "01" : "02";
  const numeroDeLasNotas = noAplica ? "02" : "03";

  return (
    <div className="flex flex-col gap-3.5">
      {noAplica ? null : (
        <section className="border-success-dot/25 bg-success-soft/50 flex flex-wrap items-center gap-3.5 rounded-xl border-[1.5px] px-4 py-3">
          <span className="bg-success-soft text-success-soft-foreground flex size-8.5 flex-none items-center justify-center rounded-lg">
            <SparklesIcon className="size-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-ink text-[0.8125rem] font-bold">
              Cargar instalaciones desde los documentos
            </h3>
            <p className="text-ink-muted mt-0.5 text-[0.71875rem] font-semibold text-pretty">
              La IA lee los documentos de Antecedentes legales y de Antecedentes históricos y
              propone las instalaciones y obras que encuentre.
              <br />
              Nada se guarda hasta que revises lo propuesto y aprietes «Guardar cambios».
            </p>
          </div>
          <div className="flex-none">
            <BotonDeRellenoConIa
              rellenando={rellenar.isPending}
              disabled={guardando || ocupada || noAplica}
              variante="oscura"
              onRellenar={pedirElRelleno}
            />
          </div>
        </section>
      )}

      <AvisoDeLoRellenado
        rellenando={rellenar.isPending}
        error={errorDelRelleno}
        aviso={aviso}
        onDescartar={propuestas.length > 0 ? descartarElRelleno : null}
        // LO PROPUESTO SE GUARDA CON LA PESTAÑA, no fila por fila, así que el
        // aviso tiene que mandar al botón de abajo. Decía "cada una se guarda
        // al agregarla" y eso describía el modelo anterior: mandaba a apretar
        // un botón que ya no existe, y contradecía al panel que está justo
        // debajo.
        queHacer={
          "Nada se guardó todavía. Revisa lo propuesto abajo y quita lo que no "
          + "corresponda; las obras que queden se guardan al apretar «Guardar "
          + "cambios», al pie de la pestaña."
        }
      />

      {noAplica ? null : (
        /* LAS COLUMNAS SE DECLARAN ACÁ: son lo único propio de esta pestaña. El
           panel aporta las decisiones —no esconder lo repetido, avisar que
           agregar guarda— que comparten las pestañas con cuadro. */
        <FilasPropuestas
          filas={propuestas}
          titulo={(cantidad) =>
            cantidad === 1 ? "1 obra propuesta por la IA" : `${cantidad} obras propuestas por la IA`
          }
          yaEsta={(obra) => yaEstaEnElCuadroDeObras(obra.tipo, filas)}
          fuente={(fila) => fila.fuente}
          ocupada={ocupada}
          onQuitar={(obra) => setPropuestas((actuales) => actuales.filter((otra) => otra !== obra))}
          columnas={[
            { encabezado: "Tipo de obra", celda: (o) => o.tipo },
            { encabezado: "Año", celda: (o) => o.anio ?? "—" },
            {
              encabezado: "Cantidad",
              // `String()` porque los formateadores de `lib/montos` reciben el
              // string de un `decimal:*` de Eloquent, y una PROPUESTA trae
              // números: es lo que respondió el modelo.
              celda: (o) => formatearCifra(o.cantidad === null ? null : String(o.cantidad)),
              numerica: true,
            },
            {
              // "UF/un." y no "UF" a secas, igual que la tabla: al lado va una
              // columna que es un total.
              encabezado: "Reposición UF/un.",
              celda: (o) => formatearMonto(o.valor_reposicion_uf_un, null),
              numerica: true,
            },
            {
              encabezado: "Comercial UF/un.",
              celda: (o) => formatearMonto(o.valor_comercial_uf_un, null),
              numerica: true,
            },
          ]}
        />
      )}

      <section className="bg-card flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-4 py-3">
        <label
          title={hayFilas ? "Quita primero las instalaciones de la lista" : undefined}
          className={cn(
            "flex w-fit items-center gap-2.5",
            hayFilas && !noAplica ? "cursor-not-allowed opacity-55" : "cursor-pointer",
          )}
        >
          <Checkbox
            checked={noAplica}
            // Con filas cargadas el registro se contradiría solo, y el servidor
            // lo rechaza con un 422. Se apaga acá para que el rechazo no sea la
            // forma de enterarse.
            disabled={guardando || hayFilas}
            onCheckedChange={(v) => setNoAplica(v === true)}
          />
          <span className="text-[0.8125rem] font-bold">
            La propiedad no cuenta con instalaciones ni obras complementarias
          </span>
        </label>
        {noAplica || hayFilas ? (
          <span className="text-ink-muted text-[0.71875rem] font-semibold">
            {noAplica
              ? "La sección se imprimirá indicando que no hay instalaciones ni obras complementarias."
              : "Para marcarlo, quita antes las instalaciones de la lista."}
          </span>
        ) : null}
      </section>

      {porEnlazar.length > 0 && !noAplica ? (
        <section
          aria-label="Instalaciones y obras creadas en terreno"
          className="bg-info-soft/50 border-info-dot/30 flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-4 py-3"
        >
          <span aria-hidden="true" className="bg-info-dot size-2 flex-none rounded-full" />
          <div className="min-w-0 flex-1">
            <h3 className="text-ink text-[0.8125rem] font-bold">{tituloDelAviso(porEnlazar.length)}</h3>
            <p className="text-ink-body mt-0.5 text-[0.71875rem] font-semibold text-pretty">
              Llegan desde la App con el tipo y los datos que el encargado alcanzó a anotar. Completa
              la valorización a partir de sus fotos y audios, o indica si es la misma que una ya
              cargada desde documentos.
            </p>
          </div>
        </section>
      ) : null}

      {noAplica ? null : (
        <ListaDeInstalaciones
          filas={filas}
          ocupada={ocupada}
          onAgregar={onAgregar}
          onAbrir={(fila) => setAbierta(fila.id)}
          // SE GUARDA SOLA y viaja sin el resto de los campos, para no pisar
          // lo que otro pudo haber corregido mientras tanto.
          onValidar={(fila, validadaAhora) => onCorregir(fila.id, { validada: validadaAhora })}
          terreno={terrenoDeLaLista}
        />
      )}

      <section
        id="instalaciones-descripcion"
        className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5"
      >
        <EncabezadoNumerado
          numero={numeroDeLaDescripcion}
          idDelTitulo="instalaciones-descripcion-titulo"
          titulo="Descripción de las instalaciones y obras complementarias"
          bajada="Texto general que se imprime en el informe: losas, redes, cierros y obras civiles."
        />
        <div className="mt-3.5 flex flex-col gap-2">
          <Label htmlFor="descripcion-instalaciones" className="sr-only">
            Descripción de las instalaciones y obras complementarias
          </Label>
          <CampoConAsistencia
            id="descripcion-instalaciones"
            campo={CAMPOS_CON_ASISTENCIA.descripcionInstalaciones}
            value={descripcion}
            onChange={setDescripcion}
            informeId={informe.id}
            rows={5}
            variantDeMejora="outline"
            placeholder="Describe en general las instalaciones y obras complementarias presentes en la propiedad…"
            disabled={guardando}
            // La misma casilla que las otras pestañas, y por lo mismo: este
            // texto lo puede haber propuesto la IA, y alguien tiene que hacerse
            // responsable de que dice lo que corresponde.
            accionesDespues={
              <label
                className={cn(
                  "flex w-fit cursor-pointer items-center gap-2 rounded-md border-[1.5px] px-2.5 py-1.5 text-[0.71875rem] font-bold",
                  validada
                    ? "bg-success-soft/50 border-success-dot/40 text-success-soft-foreground"
                    : "bg-background text-ink-muted",
                )}
              >
                <Checkbox
                  checked={validada}
                  disabled={guardando}
                  onCheckedChange={(v) => setValidada(v === true)}
                />
                Información validada
              </label>
            }
          />
          {fuenteDelTexto.visible ? (
            <FuenteDeLaPropuesta fuente={fuenteDelTexto.visible.fuente} />
          ) : null}
          <TerrenoDeLaDescripcion
            informeId={informe.id}
            terreno={terreno}
            campo={campoDeLaDescripcionDeInstalaciones(registroDeTerreno)}
            campoRedactable="instalaciones"
            valorActual={descripcion}
            asignado={asignado}
            onAbrirFoto={setFotoAbierta}
            onRedactado={(texto) => {
              // Lo escribió un modelo: nadie lo revisó todavía, y la fuente que
              // declaró el relleno anterior ya no es la de este texto.
              fuenteDelTexto.olvidar();
              setDescripcion(texto);
              setValidada(false);
            }}
          />
        </div>
      </section>

      <section className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
        <EncabezadoNumerado
          numero={numeroDeLasNotas}
          idDelTitulo="instalaciones-notas-titulo"
          titulo="Notas u observaciones"
          bajada="Opcional."
        />
        <div className="mt-3.5">
          <NotasDeLaPestana
            id="notas-instalaciones"
            value={notas}
            onChange={setNotas}
            disabled={guardando}
            plana
          />
        </div>
      </section>

      {/* LA BARRA VA AL PIE de la pestaña, como en Construcciones. Cuenta lo
          que es de la PESTAÑA (casilla, descripción, validación, notas y cada
          propuesta de la IA); las filas no entran porque ya están guardadas. */}
      <BarraDeGuardado
        cambios={cambios}
        enviando={guardando}
        etiquetaEnvio="Guardar cambios"
        ayuda="Las instalaciones y sus fotos se guardan solas al agregarlas o editarlas."
        onGuardar={guardar}
        onDescartar={descartarLosCambios}
      />

      <DialogoDeInstalacion
        fila={enEdicion}
        informeId={informe.id}
        ocupada={ocupada}
        onCorregir={onCorregir}
        onQuitar={onQuitar}
        onCerrar={() => setAbierta(null)}
        onSubirFoto={onSubirFoto}
        onQuitarFoto={onQuitarFoto}
        onLeyendaDeFoto={onLeyendaDeFoto}
      />

      {registroDeTerreno ? (
        <VisorDeFotos
          informeId={informe.id}
          fotos={fotosDelRegistro}
          abierta={fotoAbierta}
          onCambiar={setFotoAbierta}
        />
      ) : null}
    </div>
  );
}
